/*
 * Client resolution for BJC assignment links (plans/done/bjc-as-material.md;
 * the optional assignment pin is plans/bjc-shared-materials.md).
 *
 * The public BJC curriculum (/bjc/) marks each assignment link as
 * `<a class="bhs-assignment" data-material="coding/snap/…">` with an inert
 * placeholder href; a link whose material is shared by several assignments
 * also carries `data-assignment="<kind>/<name>"` naming which one it means.
 * This script asks the server, in one batch per page, where each of those
 * materials lives for the current viewer and upgrades the links:
 *
 *   assigned   -> the /c/<course>/... assignment (real, graded, gated; also
 *                 what teachers get once a section of theirs has it open)
 *   preview    -> the /m/... material preview (teachers, before it's open —
 *                 with a server-sent tooltip saying why: "Not assigned" /
 *                 "Assigned — visible at ...")
 *   unassigned -> an inline disabled state ("Not assigned in your class yet")
 *
 * The site requires JS anyway, so there is no non-JS fallback to preserve;
 * links start inert and stay in the disabled state on any fetch failure, so a
 * placeholder href never navigates and the curriculum never dead-links.
 */

import { apiError, apiFetch, errorDetail } from './modules/fetch-helpers.ts';

type Resolution = {
  status: 'assigned' | 'preview' | 'unassigned';
  href?: string;
  // Teacher-facing tooltip on preview links: why this isn't the live
  // assignment ("Not assigned" / "Assigned — visible at …").
  title?: string;
  // The static text a `data-autotitle` link fills in: the assignment's title
  // for a pinned link, else the material's. Present whenever a title exists,
  // whatever the per-viewer status.
  autoTitle?: string;
};

const SELECTOR = 'a.bhs-assignment[data-material]';
const UNASSIGNED_CLASS = 'bhs-assignment-unassigned';
const UNASSIGNED_TITLE = 'Not assigned in your class yet';

// The resolve call failed: the links stay inert, but honestly labelled —
// "couldn't check", not "not assigned yet".
const markUnknown = (a: HTMLAnchorElement, why: string) => {
  a.title = `Couldn't check whether this is assigned (${why}) — reload to try again`;
};

/*
 * A link's resolution key: its material, plus `@<kind>/<name>` when the link
 * pins a shared material to one assignment. `@` can't appear in the
 * slug-shaped paths, and the server keys its response by the exact token.
 */
const tokenFor = (a: HTMLAnchorElement): string | undefined => {
  const material = a.dataset.material;
  if (!material) return undefined;
  return a.dataset.assignment ? `${material}@${a.dataset.assignment}` : material;
};

const markUnassigned = (a: HTMLAnchorElement) => {
  a.classList.add(UNASSIGNED_CLASS);
  a.setAttribute('aria-disabled', 'true');
  a.title = UNASSIGNED_TITLE;
  // No href: an <a> with no href isn't a link, so clicks do nothing and the
  // placeholder "#" can't jump the page.
  a.removeAttribute('href');
  a.style.cursor = 'not-allowed';
  a.style.opacity = '0.6';
};

/*
 * Fill a `data-autotitle` link's text with its resolved title (the
 * assignment's for a pinned link, else the material's), once its resolution is
 * in hand. Author-labelled links (no data-autotitle) are left untouched; on a
 * miss (unknown material/assignment, no title) the "Assignment" fallback
 * already in the DOM stays. Uses textContent so the title is inert text, and
 * clears the attribute so a re-run is idempotent. Independent of the
 * per-viewer status — an autotitled link gets its title whether it's assigned,
 * preview, or unassigned.
 */
const applyAutoTitle = (a: HTMLAnchorElement, r: Resolution) => {
  if (!a.hasAttribute('data-autotitle')) return;
  if (r.autoTitle) a.textContent = r.autoTitle;
  a.removeAttribute('data-autotitle');
};

const apply = (a: HTMLAnchorElement, r: Resolution) => {
  if ((r.status === 'assigned' || r.status === 'preview') && r.href) {
    a.classList.remove(UNASSIGNED_CLASS);
    a.removeAttribute('aria-disabled');
    a.title = r.title ?? '';
    a.style.cursor = '';
    a.style.opacity = '';
    a.href = r.href;
    // Open the assignment/preview in a new tab so the curriculum page stays put
    // (matching the old external-Snap! links, which opened in a new tab).
    a.target = '_blank';
    a.rel = 'noopener';
  } else {
    markUnassigned(a);
  }
};

const resolve = async () => {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(SELECTOR));
  if (links.length === 0) return;

  // Start every link inert so a failed/slow fetch leaves a sensible state.
  links.forEach(markUnassigned);

  const tokens = Array.from(new Set(links.map(tokenFor).filter((t): t is string => Boolean(t))));
  if (tokens.length === 0) return;

  const query = tokens.map((t) => `m=${encodeURIComponent(t)}`).join('&');
  let resolved: Record<string, Resolution>;
  try {
    // GET (a pure read); same-origin fetch sends the session cookie.
    const resp = await apiFetch(`/api/bjc/resolve?${query}`, {
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) throw await apiError(resp);
    resolved = (await resp.json()) as Record<string, Resolution>;
  } catch (e) {
    // Offline / error: keep the inert state, but say what it means.
    console.error('Resolving BJC assignment links failed:', e);
    const why = errorDetail(e);
    links.forEach((a) => markUnknown(a, why));
    return;
  }

  for (const a of links) {
    const t = tokenFor(a);
    const r = t ? resolved[t] : undefined;
    if (r) {
      apply(a, r);
      applyAutoTitle(a, r);
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', resolve);
} else {
  void resolve();
}
